import React, {
  createContext,
  useContext,
  ReactNode,
  useState,
  useEffect,
  useMemo,
} from 'react';
import {useClientsContext, getLocationFilterParams} from '../clients/ClientsContextProvider';
import {FormsListResponse} from '../../pages/forms/forms';
import QUERY_KEYS from '../../constants/queryKeys';
import {useQuery} from '@tanstack/react-query';
import {
  FormsData,
  FormsResponse,
  FormStatuses,
  handleGetAllFormsService,
  handleGetFormsByIdsService,
  handleGetFormWithAnswersService,
} from '../../services/operations/forms';
import {useDebounce} from '../../hooks/useDebunce';
import {useLocation, useNavigate} from 'react-router';

type DaySummaryFormFilter = {
  formIds: string[];
  clearLabel?: string;
  clientId?: string | null;
  branchId?: string | null;
  subBranchId?: string | null;
} | null;

interface FormsContextType {
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  searchText: string;
  setSearchText: React.Dispatch<React.SetStateAction<string>>;
  selectedForms: Array<string | number>;
  setSelectedForms: React.Dispatch<
    React.SetStateAction<Array<string | number>>
  >;
  selectedForm: any;
  setSelectedForm: React.Dispatch<React.SetStateAction<any>>;
  showAddEditDialog: boolean;
  setShowAddEditDialog: React.Dispatch<React.SetStateAction<boolean>>;
  copyMode: boolean;
  setCopyMode: React.Dispatch<React.SetStateAction<boolean>>;

  allForms: FormsListResponse | undefined;
  refetchAllForms: () => void;
  menuSelectedItem: string;
  setMenuSelectedItem: React.Dispatch<React.SetStateAction<string>>;
  daySummaryFormFilter: DaySummaryFormFilter;
  clearDaySummaryFormFilter: () => void;

  formData: any;
  selectedResponseTab: string;
  setSelectedResponseTab: React.Dispatch<React.SetStateAction<string>>;
}

const FormsContext = createContext<FormsContextType | null>(null);

interface FormsProviderProps {
  children: ReactNode;
}

export const FormsProvider = ({children}: FormsProviderProps) => {
  const {selectedLocation} = useClientsContext();
  const location = useLocation();
  const navigate = useNavigate();

  const [page, setPage] = useState(1);
  const [searchText, setSearchText] = useState('');
  const [selectedForms, setSelectedForms] = useState<Array<string | number>>(
    [],
  );
  const [selectedForm, setSelectedForm] = useState<any>(null);
  const [showAddEditDialog, setShowAddEditDialog] = useState(false);
  const [copyMode, setCopyMode] = useState(false);
  const [menuSelectedItem, setMenuSelectedItem] = useState('');
  const [selectedResponseTab, setSelectedResponseTab] =
    useState<string>('question');

  const daySummaryFormFilter = useMemo<DaySummaryFormFilter>(() => {
    const state = location.state as any;
    const validFormIds: string[] = Array.isArray(state?.daySummaryFormIds)
      ? state.daySummaryFormIds.filter(
          (id: unknown): id is string => typeof id === 'string' && !!id,
        )
      : [];
    const formIds = Array.from(new Set<string>(validFormIds));

    if (!formIds.length) return null;

    return {
      formIds,
      clearLabel: state?.daySummaryClearLabel,
      clientId: state?.daySummaryClientId,
      branchId: state?.daySummaryBranchId,
      subBranchId: state?.daySummarySubBranchId,
    };
  }, [location.state]);

  const clearDaySummaryFormFilter = () => {
    navigate('/forms', {replace: true, state: null});
  };

  const daySummaryFormIdsKey = daySummaryFormFilter?.formIds.join(':') ?? '';

  const {data: formData} = useQuery({
    queryKey: [QUERY_KEYS.GET_FORM_WITH_ANSWERS, selectedForm],
    queryFn: () =>
      handleGetFormWithAnswersService({
        formId: selectedForm!,
      }),
    enabled: !!selectedForm,
    staleTime: 0,
  });

  const debouncedSearch = useDebounce(searchText, 500);

  const {data: allFormsData, refetch} = useQuery<FormsResponse>({
    queryKey: [
      daySummaryFormFilter
        ? QUERY_KEYS.GET_FORMS_BY_IDS
        : QUERY_KEYS.GET_ALL_FORMS,
      page,
      debouncedSearch,
      selectedLocation?.id,
      daySummaryFormIdsKey,
    ],
    queryFn: () => {
      if (daySummaryFormFilter) {
        return handleGetFormsByIdsService({
          formIds: daySummaryFormFilter.formIds,
          page,
          limit: 50,
          isPaginated: true,
          search: debouncedSearch,
          ...(daySummaryFormFilter.clientId && {
            clientId: daySummaryFormFilter.clientId,
          }),
          ...(daySummaryFormFilter.branchId && {
            branchId: daySummaryFormFilter.branchId,
          }),
          ...(daySummaryFormFilter.subBranchId && {
            subBranchId: daySummaryFormFilter.subBranchId,
          }),
        });
      }

      return handleGetAllFormsService({
        page,
        limit: 50,
        isPaginated: true,
        search: debouncedSearch,
        ...getLocationFilterParams(selectedLocation),
      });
    },
    enabled: daySummaryFormFilter ? daySummaryFormFilter.formIds.length > 0 : !!selectedLocation,
  });

  // Filter mock data based on selected location and search
  const allForms = useMemo<FormsListResponse>(() => {
    let filteredForms = allFormsData?.data || [];
    // Filter by status (exclude archived unless archived mode is enabled)
    if (menuSelectedItem !== FormStatuses.ARCHIVED) {
      filteredForms = filteredForms.filter(
        form => form.status !== FormStatuses.ARCHIVED,
      );
    }

    return {
      forms: filteredForms,
      total: filteredForms.length,
      totalPages: Math.ceil(
        filteredForms.length / (allFormsData?.pagination?.limit || 50),
      ),
      page: page,
      limit: allFormsData?.pagination?.limit || 50,
    };
  }, [selectedLocation, debouncedSearch, menuSelectedItem, page, allFormsData]);

  const refetchAllForms = () => {
    refetch();
  };

  useEffect(() => {
    setPage(1);
    setSelectedForms([]);
  }, [selectedLocation, daySummaryFormIdsKey]);

  return (
    <FormsContext.Provider
      value={{
        page,
        setPage,
        searchText,
        setSearchText,
        selectedForms,
        setSelectedForms,
        selectedForm,
        setSelectedForm,
        showAddEditDialog,
        setShowAddEditDialog,
        copyMode,
        setCopyMode,

        allForms,
        refetchAllForms,

        menuSelectedItem,
        setMenuSelectedItem,
        daySummaryFormFilter,
        clearDaySummaryFormFilter,

        selectedResponseTab,
        setSelectedResponseTab,

        formData,
      }}>
      {children}
    </FormsContext.Provider>
  );
};

export const useFormsContext = () => {
  const context = useContext(FormsContext);
  if (!context) {
    throw new Error('useFormsContext must be used within a FormsProvider');
  }
  return context;
};
